import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import {
  ADOBE_LINK_NAME
} from '../../../../common/AdobeTagging/adobeConstants';
import { buildUserSelections } from '../../../../common/AdobeTagging/adobeUtils';
import { Disposition } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../common/Utils';
import { useSaveOfferDisposition } from '../../../../hooks/useCeOffers';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  getCurrentMember,
  getEnterpriseId,
} from '../../../../utils/user.utils';

interface ProgramPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOfferName: string | null;
  programPromotions: any;
  keywordSearchTerm?: string;
}

export const ProgramPromotionModal: React.FC<ProgramPromotionModalProps> = ({
  isOpen,
  onClose,
  selectedOfferName,
  programPromotions,
  keywordSearchTerm = '',
}) => {
  const { t } = useTranslation();
  const data = useLagoon('ui-messaging')();
  const modalContent = find(data, {
    key: ConstantsLagoon.PROGRAM_PROMOTION.PROGRAM_PROMOTION_MODAL_CONTENT,
  });

  const [, saveOfferDisposition] = useSaveOfferDisposition({});
  const currentMember = getCurrentMember();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enableAnalytics: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION_ANALYTICS
  );

  // Track modal view when it opens
  useEffect(() => {
    if (isOpen && selectedOfferName && enableAnalytics) {
      adobeModalTrackEvent(modalContent.title);
    }
  }, [isOpen, selectedOfferName, keywordSearchTerm, enableAnalytics]);

  const handleDecline = () => {
    if (selectedOfferName) {
      const offer = programPromotions.find(
        (o) => o.offerName === selectedOfferName
      );
      if (offer?.offerToken) {
        const enterpriseId = getEnterpriseId(currentMember);

        if (enterpriseId) {
          saveOfferDisposition({
            variables: {
              enterpriseId,
              disposition: Disposition.DECLINED,
              offerToken: offer.offerToken,
            },
          });
        }
      }
    }
  };

  const trackModalButtonClick = (
    buttonName: string,
    destinationUrl: string = ''
  ) => {
    if (!enableAnalytics) return;

    adobeLinkTrackEvent({
      name: buttonName,
      location: modalContent?.title,
      destinationUrl,
      userSelections: buildUserSelections(
        keywordSearchTerm,
        selectedOfferName || ''
      ),
    });
  };

  return (
    <Modal
      css={{
        'abyss-modal-content-container': {
          width: '572px',
        },
      }}
      data-testid="program-promotion-modal"
      isOpen={isOpen}
      onClose={() => {
        handleDecline();
        trackModalButtonClick(
          ADOBE_LINK_NAME.CLOSE
        );
        onClose();
      }}
      title={modalContent?.title}
    >
      <Modal.Section data-testid="program-promotion-modal-message">
        {modalContent?.message}
      </Modal.Section>
      <Modal.Section>
        <Layout.Group alignLayout="right">
          <Button
            data-testid="cancel-button"
            onClick={() => {
              handleDecline();
              trackModalButtonClick(
                ADOBE_LINK_NAME.CANCEL
              );
              onClose();
            }}
            variant="outline"
          >
            {t('THINGS_TO_CONSIDER_MODAL.CANCEL_BUTTON')}
          </Button>
          <Button
            data-testid="continue-button"
            onClick={() => {
              if (selectedOfferName) {
                const offer = programPromotions.find(
                  (o) => o.offerName === selectedOfferName
                );
                if (offer?.cta?.ctaValue) {
                  const enterpriseId = getEnterpriseId(currentMember);
                  if (offer?.offerToken && enterpriseId) {
                    saveOfferDisposition({
                      variables: {
                        enterpriseId,
                        disposition: Disposition.ACCEPTED,
                        offerToken: offer.offerToken,
                      },
                    });
                  }

                  trackModalButtonClick(
                    ADOBE_LINK_NAME.CONTINUE,
                    offer.cta.ctaValue
                  );
                  window.open(offer.cta.ctaValue, '_blank');
                }
              }
              onClose();
            }}
          >
            {t('THINGS_TO_CONSIDER_MODAL.CONTINUE_BUTTON')}
          </Button>
        </Layout.Group>
      </Modal.Section>
    </Modal>
  );
};
